import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle';
import React, { Component, PropsWithChildren, ReactElement, RefObject, SyntheticEvent } from 'react';
import Scrollbar from 'react-custom-scrollbars';
import isEqual from 'react-fast-compare';
import ReactTable, { Column, ReactTableDefaults } from 'react-table';
import 'react-table/react-table.css';
import styled from 'styled-components';
import { IFilterValues } from '../../Filter';
import { Icon } from '../../Icon';
import { SelectionHandler } from '../../utils/SelectionHandler';
import { GridExport } from '../GridExport';
import { GridFilter } from '../GridFilter';
import { GridFolder } from '../GridFolder';
import { GridSearch } from '../GridSearch';
import { IDataGridBaseData } from '../types';
import './DataGrid.scss';
import { DataGridCell } from './DataGridCell';
import { DataGridContext } from './dataGridContext';
import { DataGridLoading } from './DataGridLoading';
import { DataGridPagination } from './DataGridPagination';
import { DataGridRow } from './DataGridRow';
import { DataGridSkeleton } from './DataGridSkeleton';
import { DataGridTable } from './DataGridTable';
import { DataGridTableBody } from './DataGridTableBody';
import { DataGridTh } from './DataGridTh';
import { DataGridThead } from './DataGridThead';
import {
  ColumnVisibilityRule,
  DataGridColumn,
  FinalState,
  IDataGridFetchState,
  IDataGridLoadingProps,
  IDataGridProps,
  IDataGridRow,
  IDataGridState,
  IInternalDisplayPanelProps,
  IReactTableFilter,
  IRowData,
  PageRow,
} from './types';
import { getInitialState, getSelectionUpdateState } from './utils';

const DataGridHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const GridHeader = styled.div`
  display: flex;
  align-items: center;

  .hlc-datagrid-header {
    margin: 0;
  }
`;

export class DataGrid<T extends IDataGridBaseData> extends Component<
  PropsWithChildren<IDataGridProps<T>>,
  IDataGridState<T>
> {
  public static getDerivedStateFromProps<T extends IDataGridBaseData>(
    nextProps: IDataGridProps<T>,
    state: IDataGridState<T>,
  ) {
    if (
      nextProps.selected &&
      !isEqual(
        nextProps.selected,
        state.selectionHandler.selected.map((row) => row.id),
      )
    ) {
      const selectedRows = nextProps.selected.map((id) => {
        const actualRow = nextProps.data.find((item) => item.id === id);
        return actualRow || ({ id } as T);
      });
      const newHandler = new SelectionHandler(selectedRows);
      return {
        ...state,
        ...getSelectionUpdateState(newHandler, nextProps, state),
      };
    }

    if (
      nextProps.columns.length !== state.columns.length ||
      !nextProps.columns.every(
        (newColumn) => !!state.columns.find((oldColumn) => oldColumn.accessor === newColumn.accessor),
      )
    ) {
      return getInitialState(nextProps);
    }

    if (nextProps.totalRecords !== undefined && nextProps.totalRecords !== -1) {
      const start = state.page * state.pageSize + 1;
      const weCantEvenBeOnThisPage = start > nextProps.totalRecords;

      if (weCantEvenBeOnThisPage) {
        const pageWeShouldBeOn =
          nextProps.totalRecords > 0 ? Math.floor(nextProps.totalRecords / state.pageSize) - 1 : 0;

        return {
          ...state,
          page: pageWeShouldBeOn,
        };
      }
    }

    return null;
  }
  public readonly state: Readonly<IDataGridState<T>> = getInitialState(this.props);
  private tableState!: FinalState<T>;

  private headerRef: RefObject<HTMLElement> | null = null;

  private scrollbarRef: Scrollbar | null = null;
  private shouldResetScrollAfterUpdate = false;

  public componentDidUpdate(prevProps: IDataGridProps<T>) {
    const { selected } = this.state.selectionHandler;
    if (
      !this.state.multiSelectActive &&
      selected.length > 0 &&
      this.state.isRefreshing &&
      prevProps.data !== this.props.data
    ) {
      const row = this.props.data.filter((item) => {
        return item.id === selected[0].id;
      });
      if (row.length > 0) {
        const newSelectionHandler = new SelectionHandler<T>(row);
        this.setState((state) => ({
          ...state,
          isRefreshing: false,
          selectionHandler: newSelectionHandler,
        }));
      } else {
        this.setState((state) => ({
          ...state,
          isRefreshing: false,
          selectionHandler: new SelectionHandler<T>(),
        }));
      }
    }

    if (this.shouldResetScrollAfterUpdate) {
      // Reset scroll once loading overlay is hidden
      // Checking "data" is a fallback when "loading" is not used
      if ((prevProps.loading !== this.props.loading && !this.props.loading) || prevProps.data !== this.props.data) {
        this.resetScroll();
        this.shouldResetScrollAfterUpdate = false;
      }
    }
  }

  public render() {
    const { searchFilter, columns: originalColumns, pageSize, pageSizeOptions, page } = this.state;
    const {
      data,
      selectable = true,
      multiSelect = false,
      resizableColumns = false,
      totalRecords = this.getRecordCount(),
      loading,
      'qa-id': qaID = 'data-grid-qa',
      unselectableRowsGreyedOut,
    } = this.props;

    const multiSelectActive = this.state.multiSelectActive;

    const pages = totalRecords ? Math.ceil(totalRecords / pageSize) : -1;
    const fetchDataProps = this.props.onFetchData
      ? {
          manual: true,
          multiSort: false,
          onFetchData: this.handleDataFetch,
          pages,
        }
      : {};

    const selectColumn = this.selectColumn();

    const columns =
      multiSelect !== 'never' && this.state.multiSelectActive
        ? [selectColumn, ...originalColumns]
        : [...originalColumns];

    return (
      <ReactTable
        columns={columns}
        data={data}
        filtered={searchFilter}
        column={{
          ...ReactTableDefaults.column,
          filterAll: true,
          resizable: resizableColumns,
        }}
        // incorrect typings. this can return an array if filterAll prop (above) is true
        // tslint:disable-next-line
        defaultFilterMethod={this.handleLocalFilter as any}
        {...fetchDataProps}
        onSortedChange={this.props.onSortChange}
        loading={loading}
        showPagination={!this.props.hidePagination}
        pageSize={pageSize}
        page={page}
        pageSizeOptions={pageSizeOptions}
        defaultSorted={this.state.sort}
        getTdProps={(
          state: FinalState<T>,
          rowInfo: { row: IDataGridRow<T> & T } | undefined,
          column: DataGridColumn | undefined,
        ) => {
          const sortedColumnAccessors =
            state.sorted && state.sorted.length > 0 ? state.sorted.map((sortedColumn) => sortedColumn.id) : [];
          const thisColumnsAccessor = column ? column.id : undefined;
          const sorted = !!sortedColumnAccessors.find((accessor: string) => accessor === thisColumnsAccessor);
          return {
            sorted,
          };
        }}
        getTheadProps={() => {
          return {
            registerHeaderRef: (hdRef: RefObject<HTMLDivElement>) => {
              this.headerRef = hdRef;
            },
          };
        }}
        getTbodyProps={() => {
          return {
            registerScrollBarRef: (sbRef: Scrollbar) => {
              this.scrollbarRef = sbRef;
            },
            syncTableHeaderOffset: (offset: number) => {
              if (this.headerRef) {
                (this.headerRef as any).style.marginLeft = `-${offset}px`;
              }
            },
          };
        }}
        getTrProps={(
          state: FinalState<T>,
          rowInfo: { row: IDataGridRow<T> & T } | undefined,
          column: DataGridColumn | undefined,
        ) => {
          if (!rowInfo || !selectable || !rowInfo.row) {
            return {};
          }
          const { row } = rowInfo;
          const id = row._original.id;
          return {
            onClick: (e: SyntheticEvent<HTMLDivElement>) => {
              this.handleSelection(row);
            },
            onCtrlOrCmdClick:
              this.props.multiSelect !== 'never'
                ? (e: SyntheticEvent<HTMLDivElement>) => {
                    this.handleCtrlOrCmdClick(row);
                  }
                : null,
            onDoubleClick: (e: SyntheticEvent<HTMLDivElement>) => {
              this.handleDoubleClick(row);
            },
            onShiftClick:
              this.props.multiSelect !== 'never'
                ? (e: SyntheticEvent<HTMLDivElement>) => {
                    this.handleShiftClick(row);
                  }
                : null,
            selected: this.state.selectionHandler.isSelected(id),
            style:
              row._original._rowSelectable === false
                ? { backgroundColor: unselectableRowsGreyedOut ? '#f5f5f5' : 'none' }
                : selectable
                ? { cursor: 'pointer' }
                : {},
          };
        }}
        getPaginationProps={(
          state: FinalState<T>,
          rowInfo: { row: IDataGridRow<T> & T } | undefined,
          column: DataGridColumn | undefined,
        ) => {
          return {
            multiSelect,
            multiSelectActive,
            onColumnVisibilityChange: this.handleColumnVisibilityChange,
            onPageChange: this.handlePageChange,
            onPageSizeChange: this.handlePageSizeChange,
            totalRecords,
          };
        }}
        getLoadingProps={(
          state: FinalState<T>,
          rowInfo: { row: IDataGridRow<T> & T } | undefined,
          column: DataGridColumn | undefined,
        ): IDataGridLoadingProps => {
          return {
            loading: !!this.props.loading,
            visibleColumnNames: this.getColumnNames(state.allVisibleColumns),
          };
        }}
        getTableProps={(
          state: FinalState<T>,
          rowInfo: { row: IDataGridRow<T> & T } | undefined,
          column: DataGridColumn | undefined,
        ): { displayPanelProps: IInternalDisplayPanelProps<T> } => {
          return {
            displayPanelProps: {
              ...this.getRenderProps(),
              displayPanel: this.props.displayPanel,
              selectable:
                rowInfo && rowInfo.row._original._rowSelectable === false
                  ? false
                  : this.props.selectable
                  ? this.props.selectable
                  : true,
              showDisplay: this.state.showDisplay,
            },
          };
        }}
        TheadComponent={DataGridThead}
        ThComponent={DataGridTh}
        TrComponent={DataGridRow}
        TdComponent={DataGridCell}
        PaginationComponent={DataGridPagination}
        TableComponent={DataGridTable}
        TbodyComponent={DataGridTableBody}
        LoadingComponent={DataGridLoading}
      >
        {(state: FinalState<T>, makeTable: () => JSX.Element) => {
          this.tableState = state;

          const placeholderEnabled = !!this.props.noDataPlaceholder && this.props.noDataPlaceholderActive;
          const showPlaceholder = placeholderEnabled && !this.props.loading;
          const showPlaceholderLoading = placeholderEnabled && this.props.loading;
          const showGrid = !placeholderEnabled;

          return (
            <DataGridContext.Provider value={this.getRenderProps()}>
              <div className="hlc-datagrid-wrapper" qa-id={qaID}>
                <DataGridHeaderWrapper className="hlc-datagrid-header-wrapper" qa-id={`${qaID}-header`}>
                  {this.renderGridHeader()}
                  {showGrid && this.renderGridControls()}
                </DataGridHeaderWrapper>
                {showGrid && (
                  <div className="hlc-datagrid-table-wrapper" qa-id={`${qaID}-table`}>
                    {makeTable()}
                  </div>
                )}
                {showPlaceholderLoading && (
                  <div className="hlc-datagrid-placeholder-container">
                    <DataGridSkeleton visibleColumnNames={this.getColumnNames(state.allVisibleColumns)} />
                  </div>
                )}
                {showPlaceholder && (
                  <div className="hlc-datagrid-placeholder-container">{this.props.noDataPlaceholder}</div>
                )}
              </div>

              {this.props.children}
            </DataGridContext.Provider>
          );
        }}
      </ReactTable>
    );
  }

  private renderGridHeader(): ReactElement<any> {
    const { header = '', folder, foldersEnabled, onCreate, 'qa-id': qaID } = this.props;

    let folderNameElement: React.ReactElement<any> | null = null;

    if (header && foldersEnabled) {
      const folderName = folder ? `in ${folder.name}` : 'All';
      folderNameElement = (
        <div className="hlc-datagrid-folder-name-wrapper">
          <span className="folder-name">{folderName}</span>
        </div>
      );
    }
    return (
      <GridHeader>
        {header && <h2 className="hlc-datagrid-header">{header}</h2>}
        {folderNameElement}
        {onCreate && (
          <div className="hlc-datagrid-create-icon">
            <Icon qa-id={`${qaID}-create`} title="Create" icon={faPlusCircle} onClick={this.handleCreate} size="lg" />
          </div>
        )}
      </GridHeader>
    );
  }

  private renderGridControls(): ReactElement<any> {
    const {
      hideSearch = false,
      foldersEnabled,
      folder,
      onFolderIconClick,
      'qa-id': qaID = 'data-grid-qa',
    } = this.props;
    return (
      <div className="hlc-datagrid-controls" qa-id={`${qaID}-controls`}>
        {this.props.controls && <div className="hlc-datagrid-controls-wrapper">{this.props.controls}</div>}
        {foldersEnabled && (
          <GridFolder qa-id={`${qaID}-folder`} isFolderSelected={!!folder} onClick={onFolderIconClick} />
        )}
        {!hideSearch && (
          <GridSearch qa-id={`${qaID}-search`} onChange={this.handleSearch} loading={this.props.loading} />
        )}
        {this.props.filters && (
          <GridFilter
            qa-id={`${qaID}-filter`}
            filters={this.props.filters || []}
            initialValues={
              this.props.initialState && this.props.initialState.filters ? this.props.initialState.filters : {}
            }
            onChange={this.handleFilterChange}
            setIsFiltering={this.props.setIsFiltering}
          />
        )}
        {this.props.handleDataExport && (
          <GridExport
            qa-id={`${qaID}-export`}
            handleDataExport={this.handleDataExport}
            dataCount={this.props.data.length}
          />
        )}
      </div>
    );
  }

  private handleRefresh = (retainSelection = false) => {
    if (this.state.mode === 'remote') {
      this.handleDataFetch(this.tableState);
      this.setState({ isRefreshing: true });
    }
    if (!retainSelection) {
      this.deselectAll();
    }
  };

  private handleDataExport = () => {
    if (this.props.handleDataExport) {
      this.props.handleDataExport(this.getFetchState(this.tableState));
    }
  };

  private handleCreate = () => {
    if (this.props.onCreate) {
      this.props.onCreate(this.getRenderProps());
    }
  };

  private handleSearch = (filterType: string, searchValue: string) => {
    /**
     * By default react table works with an AND search
     * We're hijacking this here and only using one column to preform an OR search with the
     * filterAll method set to true, so it doesn't matter which column we use
     * React Table only calls its filter method on columns that are visible, which is why
     * we're just grabbing the first visible to trigger our OR search
     */
    const firstVisibleColumn = this.state.columns.find((column) => column.show !== false);

    if (!firstVisibleColumn || !firstVisibleColumn.accessor || typeof firstVisibleColumn.accessor !== 'string') {
      return;
    }
    const searchFilter = [{ id: firstVisibleColumn.accessor, value: searchValue }];
    this.setState(
      {
        page: 0,
        searchFilter,
      },
      () => {
        if (!this.state.multiSelectActive) {
          this.deselectAll();
        }
      },
    );
  };
  private handleLocalFilter = (filter: IReactTableFilter, rows: PageRow<T>[], column: DataGridColumn) => {
    const { value, ids = [] } = filter;
    return rows.filter((row) => {
      if (ids.length) {
        if (!ids.includes(row._original.id)) {
          return false;
        }
      }
      if (!value) {
        return true;
      }
      for (const searchableColumn of this.state.searchableColumns) {
        if (typeof searchableColumn === 'string' && row.hasOwnProperty(searchableColumn)) {
          const rowValue = (row as Record<string, unknown>)[searchableColumn];
          if (typeof rowValue === 'string') {
            if (rowValue.toLowerCase().includes(value.toLowerCase())) {
              return true;
            }
          } else if (typeof rowValue === 'number') {
            return rowValue === parseInt(value, 10);
          }
        }
      }
      return false;
    });
  };
  private handleDataFetch = (state: FinalState<T>) => {
    if (this.props.onFetchData) {
      const tableState = this.getFetchState(state);
      this.props.onFetchData(tableState);
    }
  };

  private handleSelection = (row: PageRow<T>, forceMultiselect: boolean = false) => {
    const { selectable = true, multiSelect = 'toggleable' } = this.props;
    if (!selectable || row._original._rowSelectable === false) {
      return;
    }
    /**
     * This has to be _original because we don't actually render id in grid most of the time
     * Often it's locked/hidden, which to the base datagrid means it doesn't exist except in
     * the original data passed
     */
    const id = row._original.id;
    const newSelectionHandler = new SelectionHandler<T>(this.state.selectionHandler.selected);
    const isSelected = newSelectionHandler.isSelected(id);

    if (isSelected) {
      // clicking on an already selected row should deselect it
      newSelectionHandler.removeSelection(id);
    } else {
      if (multiSelect !== 'always' && !forceMultiselect && (multiSelect === 'never' || !this.state.multiSelectActive)) {
        const [currentlySelected] = newSelectionHandler.selected;
        if (currentlySelected !== undefined) {
          newSelectionHandler.removeSelection(currentlySelected.id);
        }
      }
      newSelectionHandler.addSelection(id, row._original);
    }

    this.updateSelected(newSelectionHandler);
  };

  private handlePageSelection = (pageIsSelected: boolean) => {
    const { page, pageSize } = this.state;
    const isLocal = this.state.mode !== 'remote';
    const data = this.tableState.sortedData;
    const pageStart = isLocal ? page * pageSize : 0;
    const pageEnd = pageStart + pageSize;
    const range = data.slice(pageStart, pageEnd);
    const newSelectionHandler = new SelectionHandler<T>(this.state.selectionHandler.selected);

    // TODO: gotta be a better way to do this
    range.forEach(({ _original: row }) => {
      if (pageIsSelected) {
        row._rowSelectable !== false && newSelectionHandler.removeSelection(row.id);
      } else {
        row._rowSelectable !== false && newSelectionHandler.addSelection(row.id, row);
      }
    });

    this.updateSelected(newSelectionHandler);
  };

  public deselectSome = (rowIds: string[]) => {
    const newSelectionHandler = new SelectionHandler<T>(this.state.selectionHandler.selected);
    rowIds.forEach((rowId) => {
      newSelectionHandler.removeSelection(rowId);
    });
    this.updateSelected(newSelectionHandler);
  };

  private deselectAll = () => {
    this.updateSelected(new SelectionHandler<T>([]));
  };

  private handleDoubleClick = (row: PageRow<T>) => {
    const { onDoubleClick } = this.props;
    if (onDoubleClick) {
      onDoubleClick(row._original);
    }
  };

  private handleCtrlOrCmdClick = (row: PageRow<T>) => {
    this.activateMultiSelectActiveState();
    this.handleSelection(row, true);
  };

  private handleShiftClick = (row: PageRow<T>) => {
    this.activateMultiSelectActiveState();
    const data = this.tableState.sortedData as PageRow<T>[];
    const previousClickedItem = this.state.selectionHandler.selected[this.state.selectionHandler.selected.length - 1];
    const indexOfClickedRow = data.findIndex((sortedDataRows) => {
      return sortedDataRows._original.id === row._original.id;
    });
    const indexOfPreviousClickedItem = data.findIndex((individualRow) => {
      return previousClickedItem ? individualRow._original.id === previousClickedItem.id : false;
    });

    if (indexOfPreviousClickedItem === -1) {
      row._original._rowSelectable !== false && this.handleSelection(row);
      return;
    }

    const selectionHandler = new SelectionHandler<T>(this.state.selectionHandler.selected);
    const start = indexOfClickedRow < indexOfPreviousClickedItem ? indexOfClickedRow : indexOfPreviousClickedItem;
    const end = indexOfClickedRow > indexOfPreviousClickedItem ? indexOfClickedRow : indexOfPreviousClickedItem;

    const range = [...data.slice(start, end + 1)];
    const rangeAlreadySelected = range.every((item) => selectionHandler.isSelected(item._original.id));
    if (rangeAlreadySelected) {
      range.forEach((item) => {
        item._original._rowSelectable !== false && selectionHandler.removeSelection(item._original.id);
      });
    } else {
      range.forEach((item) => {
        if (!selectionHandler.isSelected(item._original.id) && item._original._rowSelectable !== false) {
          selectionHandler.addSelection(item._original.id, item._original);
        }
      });
    }

    this.updateSelected(selectionHandler);
  };

  private updateSelected = (newSelectionHandler: SelectionHandler<T>) => {
    if (this.props.onSelect) {
      this.props.onSelect(
        newSelectionHandler.selected.map((item) => item.id),
        newSelectionHandler.selected,
      );
    }

    if (!this.props.selected) {
      this.setState(getSelectionUpdateState(newSelectionHandler, this.props, this.state));
    }
  };

  private handleColumnVisibilityChange = (selected: string[]) => {
    const newColumns = this.state.columns.map((column) => {
      const show = selected.findIndex((accessor) => column.accessor === accessor) !== -1;

      if (column.locked) {
        return column;
      }
      return {
        ...column,
        show,
      };
    });
    this.setState({ columns: newColumns });
    if (this.props.onColumnVisibilityChange) {
      this.props.onColumnVisibilityChange(
        newColumns.reduce((map, column) => {
          map[column.accessor as keyof T] = !!column.show;
          return map;
        }, {} as ColumnVisibilityRule<T>),
      );
    }
  };

  private handlePageChange = (page: number) => {
    this.setState({ page });

    this.handleScroll();
  };

  private handleScroll = () => {
    const isRemote = !!this.props.onFetchData;

    if (isRemote) {
      this.shouldResetScrollAfterUpdate = true;
    } else {
      this.resetScroll();
    }
  };

  private resetScroll = () => {
    if (this.scrollbarRef) {
      this.scrollbarRef.scrollToTop();
    }
  };

  private handlePageSizeChange = (newPageSize: number) => {
    const { pageSize: currentPageSize, page } = this.state;
    const row = currentPageSize * page;
    const newPage = Math.floor(row / newPageSize);

    this.setState({
      page: newPage,
      pageSize: newPageSize,
    });
  };

  private handleFilterChange = (values: IFilterValues) => {
    this.setState({ filters: values, page: 0 }, () => {
      this.handleRefresh();
      if (this.props.onFilterChange) {
        this.props.onFilterChange(values);
      }
    });
  };

  private selectColumn = (): DataGridColumn => {
    // based on https://github.com/react-tools/react-table/blob/master/src/hoc/selectTable/index.js
    return {
      Cell: (rowInfo: { row: PageRow<T> }) => {
        const id = rowInfo.row._original.id;
        const checked = this.state.selectionHandler.isSelected(id);
        return (
          <input
            type="checkbox"
            disabled={rowInfo.row._original._rowSelectable === false ? true : false}
            checked={checked}
            onChange={() => {
              // noop, handled by the click handler on the row
            }}
            style={rowInfo.row._original._rowSelectable === false ? {} : { cursor: 'pointer' }}
          />
        );
      },
      Header: (headerProps) => {
        const { data } = headerProps as unknown as { data: PageRow<T>[] };
        const { page, pageSize } = this.state;
        const start = page * pageSize;
        const end = start + pageSize;
        const filteredData = this.state.mode === 'remote' ? data : data.slice(start, end);
        const pageIsSelected = filteredData.every((row) => this.state.selectionHandler.isSelected(row._original.id));
        return (
          <input
            type="checkbox"
            checked={pageIsSelected}
            style={{ cursor: 'pointer' }}
            onChange={() => this.handlePageSelection(pageIsSelected)}
          />
        );
      },
      accessor: () => 'x',
      filterable: false,
      headerStyle: { textAlign: 'center' },
      id: '_selector',
      locked: true,
      resizable: false,
      sortable: false,
      style: { textAlign: 'center' },
      width: 30,
    };
  };

  private handleDisplayClose = () => {
    this.setState({ showDisplay: false });
  };

  private getRowData = (): IRowData<T> => {
    const { selected } = this.state.selectionHandler;
    const [firstSelected] = selected;

    return {
      multi: selected,
      single: firstSelected || ({} as T),
    };
  };

  private toggleViewSelected = () => {
    this.setState((prevState) => {
      const viewSelected = !prevState.viewSelected;
      const searchFilter: IReactTableFilter[] = [{ ...prevState.searchFilter[0], ids: [] }];
      const [filterItem] = searchFilter;
      /**
       * We're adding ids onto the search filter to use in our local filter override
       */
      if (viewSelected && this.state.mode === 'local') {
        const firstVisibleColumn = this.state.columns.find((column) => column.show !== false);

        if (firstVisibleColumn && typeof firstVisibleColumn.accessor === 'string') {
          if (!filterItem.id) {
            filterItem.id = firstVisibleColumn.accessor;
          }
          filterItem.ids = this.state.selectionHandler.selected.map((item) => item.id);
        }
      }
      return { viewSelected, searchFilter, page: 0 };
    });
  };

  private getRecordCount(): number {
    const { data, onFetchData } = this.props;
    const isRemote = !!onFetchData;

    return !isRemote ? data.length : 0;
  }

  private activateMultiSelectActiveState() {
    if (this.props.multiSelect !== 'never') {
      this.setState({ multiSelectActive: true });
    }
  }

  private getRenderProps = () => {
    return {
      deselectAll: this.deselectAll,
      deselectSome: this.deselectSome,
      handleDisplayClose: this.handleDisplayClose,
      handleRefresh: this.handleRefresh,
      multiSelectActive: this.state.multiSelectActive,
      rowData: this.getRowData(),
      toggleViewSelected: this.toggleViewSelected,
      viewSelected: this.state.viewSelected,
    };
  };

  private getFetchState = (state: FinalState<T>): IDataGridFetchState => {
    return {
      filterByIds: this.state.viewSelected,
      filters: this.state.filters,
      folderId: this.props.foldersEnabled && this.props.folder ? this.props.folder.id : null,
      page: this.state.page,
      pageSize: this.state.pageSize,
      search: this.state.searchFilter.length ? this.state.searchFilter[0].value : '',
      selected: this.state.selectionHandler.selected.map((item) => item.id),
      sort: state.sorted,
    };
  };

  private getColumnNames(columns: Column[]): string[] {
    return columns.map((c) => (typeof c.Header === 'string' ? c.Header : ''));
  }
}

export default DataGrid;
